import React, { useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import StateSelector from './StateSelector';
import { COMPLIANCE_COVERAGE_PATH, COMPLIANCE_COVERAGE_LINK_TEXT } from '../config/complianceConfig';

const SimplifiedICPEvaluator = ({ evaluationData = null }) => {
  const [responses, setResponses] = useState({});
  const [showResults, setShowResults] = useState(false);
  const resultRef = useRef();

  const defaultData = {
    title: "Kipu ICP Evaluator - Simplified",
    description: "Quick 3-question assessment for prospect evaluation",
    questions: [],
    workflows: ["overall", "crm", "emr", "billing"],
    classifications: {
      green: "Good Fit",
      yellow: "Proceed with Caution", 
      red: "Not a Good Fit"
    }
  };

  const data = evaluationData || defaultData;

  const handleResponseChange = (questionId, value, isMultiselect = false) => {
    if (isMultiselect) {
      setResponses(prev => {
        const currentSelections = prev[questionId] || [];
        const isSelected = currentSelections.includes(value);
        
        let newSelections;
        if (isSelected) {
          newSelections = currentSelections.filter(sel => sel !== value);
        } else {
          newSelections = [...currentSelections, value];
        }
        
        return {
          ...prev,
          [questionId]: newSelections
        };
      });
    } else {
      setResponses(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
  };

  const shouldShowQuestion = (question) => {
    if (!question.conditional) return true;
    
    const { depends_on, show_if_contains } = question.conditional;
    const dependentResponse = responses[depends_on];
    
    if (!dependentResponse) return false;
    
    const dependentArray = Array.isArray(dependentResponse) ? dependentResponse : [dependentResponse];
    return show_if_contains.some(value => dependentArray.includes(value));
  };

  const evaluateWorkflows = () => {
    const marketSegment = responses.market_segment;
    const serviceSegment = responses.service_segment;
    const levelsOfCare = responses.levels_of_care || [];
    const modality = responses.modality || [];
    const payerMix = responses.payer_mix || [];
    const complianceStates = responses.compliance_states || [];
    
    // Check if OTP is selected - used throughout billing rules
    const hasOTP = Array.isArray(modality) && modality.includes('op_otp');
    // Check if OBOT is selected - always recommends CollaborateMD (Good Fit)
    const hasOBOT = Array.isArray(modality) && modality.includes('op_obot');
    
    const results = {
      overall: { classification: 'green', alerts: [] },
      crm: { classification: 'green', alerts: [] },
      emr: { classification: 'green', alerts: [] },
      billing: { classification: 'green', alerts: [] },
      compliance: { classification: 'green', alerts: [] }
    };

    // Puerto Rico specific rules
    if (complianceStates.includes('PR')) {
      results.crm.classification = 'green';
      results.emr.classification = 'yellow';
      results.billing.classification = 'red';
      results.billing.alerts.push('Not a Good Fit - RCM does not support Puerto Rico');
      results.compliance.classification = 'red';
      // Compliance alert will be handled in Compliance Rules section
    }

    // United Kingdom specific rules
    if (complianceStates.includes('UK')) {
      results.crm.classification = 'green';
      results.emr.classification = 'yellow';
      results.billing.classification = 'red';
      results.billing.alerts.push('Not a Good Fit - RCM does not support United Kingdom');
      results.compliance.classification = 'red';
      // Compliance alert will be handled in Compliance Rules section
    }

    // Overall Rules
    if (levelsOfCare.includes('acute_care')) {
      results.overall.classification = 'red';
      results.overall.alerts.push('Best served by hospital systems like Epic or Cerner');
    }

    // Acute Care EMR Rules
    if (levelsOfCare.includes('acute_care')) {
      results.emr.classification = 'red';
      results.emr.alerts.push({
        text: 'Not Supported - EMR does not have all required functionality to support an Acute Care Facility',
        link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
        linkText: 'View ICP details'
      });
    }

    // Acute Care Billing Rules  
    if (levelsOfCare.includes('acute_care')) {
      results.billing.classification = 'red';
      results.billing.alerts.push('Not Supported - RCM does not have all required functionality to support an Acute Care Facility');
    }

    // CRM Rules
    if (marketSegment === 'enterprise' && (serviceSegment === 'sud' || serviceSegment === 'mental_health')) {
      results.crm.classification = 'yellow';
      results.crm.alerts.push('Could be sold, but typically, they\'d want more flexibility and integration options that could be supported by Salesforce');
    }

    // EMR Rules (skip if acute care)
    if (!levelsOfCare.includes('acute_care')) {
      if ((marketSegment === 'enterprise' || marketSegment === 'mid_market') && (serviceSegment === 'sud' || serviceSegment === 'mental_health') &&
          (levelsOfCare.includes('op_pure_play') || modality.includes('op_otp') || modality.includes('op_obot'))) {
        results.emr.classification = 'yellow';
        results.emr.alerts.push({
          text: 'Less efficient at high volume OP can proceed, alert Product about prospect',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      if (serviceSegment === 'eating_disorder') {
        results.emr.classification = 'yellow';
        results.emr.alerts.push({
          text: 'We lack Calorie Tracking, % of meal completed, Additional reporting requirements',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      if (serviceSegment === 'general_medical') {
        results.emr.classification = 'yellow';
        results.emr.alerts.push({
          text: 'Review customer details with Product for further evaluation',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      // Virtual Care alert for EMR
      if (modality.includes('virtual_care')) {
        results.emr.classification = 'red';
        results.emr.alerts.push({
          text: 'Telehealth-only provider - Alert Product team for further evaluation',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      // Telehealth informational alert
      if (modality.includes('telehealth')) {
        results.emr.alerts.push({
          text: 'Confirm provider and client are typically in the same time zone and same state.',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      // Washington DC EMR alert
      if (complianceStates.includes('DC')) {
        results.emr.classification = 'red';
        results.emr.alerts.push({
          text: 'Department of Behavioral Health in DC requires use of ONC Certified EHR',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      // Minnesota-specific EMR alert (only set to yellow if not already red)
      if (complianceStates.includes('MN') && results.emr.classification !== 'red') {
        results.emr.classification = 'yellow';
        results.emr.alerts.push({
          text: 'Minnesota requires facilities to document all treatment plan information on a single document',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      } else if (complianceStates.includes('MN') && results.emr.classification === 'red') {
        // Still add the Minnesota alert even if classification is already red
        results.emr.alerts.push({
          text: 'Minnesota requires facilities to document all treatment plan information on a single document',
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }

      // OTP state restrictions alert for EMR
      if (modality.includes('op_otp') && complianceStates.length > 0) {
      const otpSupportedStates = ['AZ', 'CA', 'DE', 'IL', 'IN', 'MD', 'NJ', 'NY', 'NC', 'ND', 'PA', 'TX', 'UT', 'VA', 'WA'];
      const otpUnsupportedStates = complianceStates.filter(state => 
        !otpSupportedStates.includes(state)
      );
      
      if (otpUnsupportedStates.length > 0) {
        const stateMap = {
          'AL': 'Alabama', 'AK': 'Alaska', 'AR': 'Arkansas', 'CO': 'Colorado', 'CT': 'Connecticut',
          'DC': 'District of Columbia', 'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
          'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine',
          'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
          'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NM': 'New Mexico',
          'OH': 'Ohio', 'OK': 'Oklahoma', 'OR': 'Oregon', 'RI': 'Rhode Island', 'SC': 'South Carolina',
          'SD': 'South Dakota', 'TN': 'Tennessee', 'VT': 'Vermont', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
          'PR': 'Puerto Rico', 'UK': 'United Kingdom'
        };
        const unsupportedStateNames = otpUnsupportedStates.map(code => stateMap[code] || code);
        
        results.emr.classification = 'yellow';
        results.emr.alerts.push({
          text: `Alert Product Team - OTP client based in ${unsupportedStateNames.join(', ')} - evaluate risks for this state`,
          link: 'https://kipusystems.atlassian.net/wiki/spaces/KMT/pages/3373171152/Ideal+Customer+Profile+ICP',
          linkText: 'View ICP details'
        });
      }
      }
    }

    // Canada billing rule
    if (complianceStates.includes('CA_COUNTRY')) {
      results.billing.classification = 'red';
      results.billing.alerts.push('Not a Good Fit - RCM does not support Canadian billing requirements');
    }

    // Billing Rules (skip if acute care and Canada)
    if (!levelsOfCare.includes('acute_care') && !complianceStates.includes('CA_COUNTRY')) {
      // Check enterprise condition but exclude tribal billing facilities (they get CollaborateMD recommendation instead)
      if (marketSegment === 'enterprise' && !payerMix.includes('tribal') && (serviceSegment === 'sud' || serviceSegment === 'mental_health') &&
          (levelsOfCare.includes('detox') || levelsOfCare.includes('residential') || 
           levelsOfCare.includes('php_iop') || levelsOfCare.includes('op_pure_play'))) {
      results.billing.classification = 'yellow';
      
      // Create specific alert with facility types
      const facilityTypes = [];
      if (levelsOfCare.includes('detox')) facilityTypes.push('Detox');
      if (levelsOfCare.includes('residential')) facilityTypes.push('Residential');
      if (levelsOfCare.includes('php_iop')) facilityTypes.push('PHP/IOP');
      if (levelsOfCare.includes('op_pure_play')) facilityTypes.push('OP Pure Play');
      
      const facilityTypeString = facilityTypes.join(' ');
      results.billing.alerts.push(`Alert Product - Facility is Enterprise ${facilityTypeString} Facility need further support to assess client fit`);
    }

    // Check if CollaborateMD will be recommended due to Medicaid restrictions
    let recommendCollaborateMD = false;
    if (payerMix.includes('medicaid') && complianceStates.length > 0) {
      const medicaidUnsupportedStates = ['AK', 'DE', 'IA', 'MN', 'DC', 'ND', 'SD'];
      recommendCollaborateMD = complianceStates.some(state => 
        medicaidUnsupportedStates.includes(state)
      );
    }

    // OTP check - when OTP is selected, show Proceed with Caution for billing workflow
    // This must happen early and set yellow classification
    if (hasOTP && !hasOBOT) {
      results.billing.classification = 'yellow';
      // Only add alert if it's not already in the alerts array
      const alertExists = results.billing.alerts.some(alert => 
        typeof alert === 'string' && alert.includes('OTP is state')
      );
      if (!alertExists) {
        results.billing.alerts.push('Proceed with Caution on KipuRCM - review with Sales Engineer as OTP is state specific');
      }
    }

    // OBOT check - when OBOT is selected (alone or with OTP), always recommend CollaborateMD as Good Fit
    if (hasOBOT) {
      results.billing.classification = 'green';
      // Remove any OTP caution alert since OBOT takes priority
      results.billing.alerts = results.billing.alerts.filter(alert =>
        !(typeof alert === 'string' && alert.includes('OTP is state'))
      );
      results.billing.alerts.push('Recommend CollaborateMD');
    }

    // Only recommend Kipu RCM if CollaborateMD is not being recommended and Tribal is not selected and OTP/OBOT are not selected
    if (!recommendCollaborateMD && !payerMix.includes('tribal') && !hasOTP && !hasOBOT &&
        (marketSegment === 'mid_market' || marketSegment === 'small_business') && (serviceSegment === 'sud' || serviceSegment === 'mental_health') &&
        (levelsOfCare.includes('detox') || levelsOfCare.includes('residential') || 
         levelsOfCare.includes('php_iop') || levelsOfCare.includes('op_pure_play'))) {
      results.billing.classification = 'green';
      results.billing.alerts.push('Recommend Kipu RCM');
    }

    // Payer Mix Alerts
    if (payerMix.includes('tribal')) {
      results.billing.alerts.push('Tribal Billing not supported in Kipu RCM - recommend CMD for billing');
    }
    if (payerMix.includes('flat_rate_eap')) {
      results.overall.classification = 'red';
      results.overall.alerts.push('Not ICP');
      results.billing.classification = 'yellow';
      results.billing.alerts.push('Proceed with caution - Alert Product - Must have Product Approval to review details of Flat Rate contracts');
    }
    if (payerMix.includes('non_traditional')) {
      results.billing.classification = 'yellow';
      results.billing.alerts.push('Proceed with caution - Alert Product - Must have Product Approval to review details of Non-Traditional contracts');
    }
    if (payerMix.includes('grant')) {
      results.billing.alerts.push('Can utilize Kipu RCM - Grants will be handled outside of KipuRCM');
    }
    if (payerMix.includes('scholarship')) {
      results.billing.alerts.push('Can utilize Kipu RCM - Scholarships will be handled outside of Kipu RCM');
    }
    if (payerMix.includes('self_pay')) {
      results.billing.alerts.push('Recommend Avea Office Patient Billing');
    }

    // Medicaid billing state restrictions - only if CollaborateMD flag was set due to Medicaid
    if (recommendCollaborateMD && payerMix.includes('medicaid') && complianceStates.length > 0) {
      const medicaidUnsupportedStates = ['AK', 'DE', 'IA', 'MN', 'DC', 'ND', 'SD'];
      const unsupportedFound = complianceStates.filter(state => 
        medicaidUnsupportedStates.includes(state)
      );
      
      if (unsupportedFound.length > 0) {
        const stateMap = {
          'AK': 'Alaska', 'DE': 'Delaware', 'IA': 'Iowa', 'MN': 'Minnesota', 
          'DC': 'District of Columbia', 'ND': 'North Dakota', 'SD': 'South Dakota',
          'PR': 'Puerto Rico', 'UK': 'United Kingdom'
        };
        const stateNames = unsupportedFound.map(code => stateMap[code] || code);
        
        results.billing.classification = 'yellow';
        results.billing.alerts.push(`Please confirm with RCM Sales Engineer support for ${stateNames.join(', ')}`);
      }
    }

      // Virtual Care alert for Billing/RCM - Not a Good Fit
      if (modality.includes('virtual_care')) {
        results.billing.classification = 'red';
        results.billing.alerts.push('Telehealth-only provider - Alert Product team for further evaluation');
      }

      // Telehealth alert for Billing/RCM
      if (modality.includes('telehealth')) {
        results.billing.classification = 'yellow';
        results.billing.alerts.push('Telehealth provider - Alert Product team for further evaluation');
      }

      // General Medical (Psychiatry) alert for Billing/RCM
      if (serviceSegment === 'general_medical') {
        results.billing.classification = 'yellow';
        results.billing.alerts.push('Do not currently have a client so please reach out to product for additional billing requirement discovery');
      }
    }

    // Compliance Rules — three-tier state coverage (worst selected tier wins)
    if (complianceStates.length > 0) {
      const fullySupportedStates = ['NV', 'UT', 'AZ', 'NM', 'TN', 'OH', 'NJ', 'DE', 'MD', 'VA', 'AL', 'GA', 'FL'];
      const partiallyBuiltStates = [
        'WA', 'OR', 'CA', 'CO', 'OK', 'TX', 'LA', 'AR', 'MO', 'IA', 'MN', 'IL',
        'IN', 'KY', 'WI', 'NC', 'SC', 'PA', 'MI', 'NY', 'DC', 'CT', 'MA', 'NH'
      ];
      const notBuiltStates = [
        'AK', 'ID', 'MT', 'ND', 'WY', 'SD', 'NE', 'KS', 'HI', 'MS', 'WV', 'RI', 'VT', 'ME'
      ];
      // Special unsupported locations that always show Not a Good Fit
      const alwaysUnsupported = ['PR', 'UK', 'CA_COUNTRY'];

      const stateMap = {
        'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
        'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'DC': 'District of Columbia',
        'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois',
        'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana',
        'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota',
        'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
        'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
        'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma', 'OR': 'Oregon',
        'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota',
        'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia',
        'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
        'PR': 'Puerto Rico', 'UK': 'United Kingdom', 'CA_COUNTRY': 'Canada'
      };

      const namesFor = (codes) => codes.map(code => stateMap[code] || code);

      const alwaysUnsupportedSelected = complianceStates.filter(state => alwaysUnsupported.includes(state));
      const notBuiltSelected = complianceStates.filter(state => notBuiltStates.includes(state));
      const partialSelected = complianceStates.filter(state => partiallyBuiltStates.includes(state));
      const fullySupportedSelected = complianceStates.filter(state => fullySupportedStates.includes(state));

      results.compliance.alerts = [];

      if (alwaysUnsupportedSelected.length > 0 || notBuiltSelected.length > 0) {
        const flagged = [...alwaysUnsupportedSelected, ...notBuiltSelected];
        results.compliance.classification = 'red';
        results.compliance.alerts.push({
          text: `Not a Good Fit - ${namesFor(flagged).join(', ')} ${flagged.length === 1 ? 'is' : 'are'} not built for Compliance`,
          link: COMPLIANCE_COVERAGE_PATH,
          linkText: COMPLIANCE_COVERAGE_LINK_TEXT
        });
      } else if (partialSelected.length > 0) {
        results.compliance.classification = 'yellow';
        results.compliance.alerts.push({
          text: `Proceed with Caution - ${namesFor(partialSelected).join(', ')} ${partialSelected.length === 1 ? 'is' : 'are'} partially built for Compliance`,
          link: COMPLIANCE_COVERAGE_PATH,
          linkText: COMPLIANCE_COVERAGE_LINK_TEXT
        });
      } else if (fullySupportedSelected.length > 0) {
        results.compliance.classification = 'green';
        results.compliance.alerts.push('Good Fit - All selected states have full compliance support through Hatch');
      }
    } else {
      // No states selected - neutral
      results.compliance.classification = 'green';
      results.compliance.alerts.push('No states selected for compliance evaluation');
    }

    // Final OTP check - MUST be absolute last before overall classification
    // When OTP is selected (without OBOT), ALWAYS show Proceed with Caution for billing workflow (unless already red)
    // OBOT takes priority over OTP and keeps billing green
    if (hasOTP && !hasOBOT && results.billing.classification !== 'red') {
      results.billing.classification = 'yellow';
    }

    // Determine overall classification from worst workflow
    const priorities = { 'red': 3, 'yellow': 2, 'green': 1 };
    const worstClassification = Object.values(results).reduce((worst, current) => {
      return priorities[current.classification] > priorities[worst] ? current.classification : worst;
    }, 'green');
    
    results.overall.classification = worstClassification;

    return results;
  };

  // Check if all required questions are answered
  const isFormValid = () => {
    const visibleQuestions = data.questions.filter(shouldShowQuestion);
    
    for (const question of visibleQuestions) {
      if (question.required) {
        const response = responses[question.id];
        
        // Check if response exists and is not empty
        if (!response) return false;
        
        // For multiselect and state_multiselect questions, check if array has items
        if ((question.type === 'multiselect' || question.type === 'state_multiselect') && 
            Array.isArray(response) && response.length === 0) return false;
        
        // For text questions, check if string is not empty
        if (question.type === 'text' && typeof response === 'string' && response.trim() === '') return false;
        
        // For radio questions, check if response exists
        if (question.type === 'radio' && !response) return false;
      }
    }
    
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowResults(true);
    // Scroll to top of page to show results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exportToPDF = async () => {
    if (!resultRef.current) return;

    try {
      const canvas = await html2canvas(resultRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 190;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 10;
      
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save('simplified-icp-evaluation.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const resetForm = () => {
    setResponses({});
    setShowResults(false);
  };

  const updateProfile = () => {
    setShowResults(false);
    // Scroll to top of page to show form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const results = showResults ? evaluateWorkflows() : null;

  const getClassificationColor = (classification) => {
    switch (classification) {
      case "green": return "bg-green-500";
      case "yellow": return "bg-yellow-500";
      case "red": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };



  const getWorkflowIcon = (workflowId) => {
    const icons = {
      overall: "🎯",
      crm: "👥",
      emr: "📋",
      billing: "💰",
      compliance: "⚖️"
    };
    return icons[workflowId] || "📊";
  };

  const getWorkflowTitle = (workflowId) => {
    const titles = {
      overall: "Overall Assessment",
      crm: "CRM Workflow",
      emr: "EMR Workflow", 
      billing: "Billing Workflow",
      compliance: "Compliance Workflow"
    };
    return titles[workflowId] || workflowId;
  };

  if (!data.questions || data.questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Kipu ICP Evaluator
          </h1>
          <p className="text-gray-600">
            Loading evaluation questions...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {data.title}
          </h1>
          {data.description && (
            <p className="text-gray-600 mb-4">
              {data.description}
            </p>
          )}
        </div>

        {!showResults ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {data.questions
              .filter(shouldShowQuestion)
              .map((question) => (
                <div key={question.id} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-700 mb-4">
                    {question.question}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </h3>
                  
                  {question.type === 'radio' && (
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <label
                          key={option.id}
                          className="flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-white transition-colors"
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.id}
                            checked={responses[question.id] === option.id}
                            onChange={() => handleResponseChange(question.id, option.id)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex-1 text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'multiselect' && (
                    <div className="space-y-2">
                      {question.options.map((option) => {
                        const isSelected = (responses[question.id] || []).includes(option.id);
                        return (
                          <label
                            key={option.id}
                            className="flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-white transition-colors"
                          >
                            <input
                              type="checkbox"
                              value={option.id}
                              checked={isSelected}
                              onChange={() => handleResponseChange(question.id, option.id, true)}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="flex-1 text-gray-700">
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {question.type === 'text' && (
                    <input
                      type="text"
                      value={responses[question.id] || ''}
                      onChange={(e) => handleResponseChange(question.id, e.target.value)}
                      placeholder={question.placeholder}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}

                  {question.type === 'state_multiselect' && (
                    <div className="space-y-2">
                      {question.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {question.description}
                        </p>
                      )}
                      <StateSelector
                        value={responses[question.id] || []}
                        onChange={(value) => handleResponseChange(question.id, value, false)}
                        placeholder="Type to search and select states..."
                      />
                    </div>
                  )}
                </div>
              ))}

            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={!isFormValid()}
                className={`font-medium py-3 px-8 rounded-lg transition-colors ${
                  isFormValid()
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Evaluate Prospect
              </button>
            </div>
          </form>
        ) : (
          <div ref={resultRef} className="space-y-8">
            {/* Workflow Results */}
            <div className="grid gap-6 md:grid-cols-2">
              {data.workflows.filter(workflowId => workflowId !== 'overall').map((workflowId) => {
                const workflow = results[workflowId];
                if (!workflow) return null;

                return (
                  <div key={workflowId} className="bg-white border-2 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">{getWorkflowIcon(workflowId)}</span>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {getWorkflowTitle(workflowId)}
                      </h3>
                    </div>
                    
                    <div className="space-y-3">
                      <div className={`text-center py-2 px-4 rounded-lg font-medium text-white ${getClassificationColor(workflow.classification)}`}>
                        {data.classifications[workflow.classification]}
                      </div>

                      {workflow.alerts.length > 0 && (
                        <div className="space-y-2">
                          {workflow.alerts.map((alert, index) => (
                            <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start flex-1">
                                  <span className="text-blue-500 mr-2 mt-0.5">ℹ️</span>
                                  <p className="text-sm text-blue-700">
                                    {typeof alert === 'string' ? alert : alert.text}
                                  </p>
                                </div>
                                {typeof alert === 'object' && alert.link && (
                                  <a
                                    href={alert.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-3 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                                    title={alert.linkText}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Response Summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Response Summary
              </h3>
              <div className="space-y-3">
                {data.questions
                  .filter(shouldShowQuestion)
                  .map((question) => {
                    const response = responses[question.id];
                    if (!response) return null;

                    let displayValue;
                    if (question.type === 'state_multiselect' && Array.isArray(response)) {
                      // Handle state codes - convert to readable format
                      const stateMap = {
                        'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                        'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'DC': 'District of Columbia',
                        'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois',
                        'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana',
                        'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota',
                        'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
                        'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
                        'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
                        'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                        'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                        'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
                        'PR': 'Puerto Rico', 'UK': 'United Kingdom', 'CA_COUNTRY': 'Canada'
                      };
                      displayValue = response.map(code => `${stateMap[code] || code} (${code})`).join(', ');
                    } else if (Array.isArray(response)) {
                      const selectedOptions = question.options.filter(opt => response.includes(opt.id));
                      displayValue = selectedOptions.map(opt => opt.label).join(', ');
                    } else if (question.type === 'text') {
                      displayValue = response;
                    } else {
                      const selectedOption = question.options.find(opt => opt.id === response);
                      displayValue = selectedOption?.label || response;
                    }

                    return (
                      <div key={question.id} className="border-b border-gray-200 pb-2">
                        <div className="font-medium text-gray-700">
                          {question.question}
                        </div>
                        <div className="text-gray-600">
                          {displayValue}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="flex justify-center space-x-4 pt-6">
              <button
                onClick={updateProfile}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Update Profile
              </button>
              <button
                onClick={exportToPDF}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Export to PDF
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Start New Evaluation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplifiedICPEvaluator;
